import { useState } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import type { DateRange } from "./types";
import { selectLogin } from "../Login/selector";
import { getAuthToken, getAuthUser } from "../../../utils/auth";
import { API_ENDPOINT } from "../../../utils/constants";
import { useSidebarCollapse } from "../../../utils/useSidebarCollapse";
import PageHeader from "../../components/PageHeader";
import Sidebar from "../../components/Sidebar";
import DashboardInjectors from "./injectors";
import WelcomeBar from "./components/WelcomeBar";
import StatsRow from "./components/StatsRow";
import RecentCalls from "./components/RecentCalls";
import { InnerLayout, PageContent, PageLayout } from "./style";

export default function DashboardPage() {
  const { data } = useSelector(selectLogin);

  const user = data ?? getAuthUser();
  const fullName = user?.full_name ?? "Guest";
  const firstName = fullName.split(" ")[0] ?? "there";

  const [collapsed, toggleCollapsed] = useSidebarCollapse();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(null);
  const [pdfExporting, setPdfExporting] = useState(false);

  async function handleExportPdf() {
    setPdfExporting(true);
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const [start, end] = dateRange ?? [null, null];

      // Range picked => filter by date. No range => base export (all call logs).
      const params = new URLSearchParams();
      if (start) params.set("start_date", start.format("YYYY-MM-DD"));
      if (end) params.set("end_date", end.format("YYYY-MM-DD"));

      const query = params.toString();
      const response = await fetch(
        `${API_ENDPOINT}dashboard/export/pdf${query ? `?${query}` : ""}`,
        { headers },
      );
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "calls_report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success("PDF report downloaded");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "PDF export failed");
    } finally {
      setPdfExporting(false);
    }
  }

  return (
    <PageLayout hasSider>
      <DashboardInjectors />
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <InnerLayout>
        <PageHeader
          routeName="Registration Dashboard"
          breadcrumbLabel="Registration Dashboard"
          onMenuClick={() => setMobileNavOpen(true)}
        />
        <PageContent>
          <WelcomeBar
            name={firstName}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onExportPdf={handleExportPdf}
            pdfExporting={pdfExporting}
          />
          <StatsRow />
          <RecentCalls dateRange={dateRange} />
        </PageContent>
      </InnerLayout>
    </PageLayout>
  );
}
